import { useCallback, useMemo, useRef } from 'react'

import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'

import { SplitsLink } from '~/components/Link'
import { Message, TextAreaInput } from '~/components/component-library'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { CSVUpload } from '~/components/forms/transfer/multi/MultiRecipientCSVUpload'
import { useMultiRecipientValidation } from '~/components/forms/transfer/multi/hooks/useMultiRecipientValidation'
import { RecipientFormValues } from '~/components/smart-accounts/schemas'
import { SupportedChainId } from '~/constants/chains'
import { useOrgCompliance } from '~/hooks/compliance'
import { useTokenMetadata } from '~/hooks/useTokenMetadata'
import { IAddress } from '~/types'

type AddMultiRecipientFormValues = {
  recipients: string
}

const PLACEHOLDER_RECIPIENTS_TEXT = `0x, 1\n0x, 1`

export const AddMultiRecipientDialog = ({
  onAdd,
  onClose,
  shouldAutoFocus,
  transferToken,
  defaultRecipients,
}: {
  onAdd: (recipients: RecipientFormValues[]) => void
  onClose: () => void
  transferToken: {
    token: IAddress
    chainId: SupportedChainId
  }
  defaultRecipients: RecipientFormValues[]
  shouldAutoFocus?: boolean
}) => {
  const formRecipientsDefault = useMemo(() => {
    return defaultRecipients.length > 0
      ? defaultRecipients
          .map(
            (recipient) => `${recipient.receiver.address}, ${recipient.amount}`,
          )
          .join('\n')
      : PLACEHOLDER_RECIPIENTS_TEXT
  }, [defaultRecipients])

  const methods = useForm<AddMultiRecipientFormValues>({
    defaultValues: { recipients: formRecipientsDefault },
  })

  const { payees, isLoading: isOrgComplianceLoading } = useOrgCompliance()
  const transferTokenMetadata = useTokenMetadata({
    address: transferToken?.token,
    chainId: transferToken?.chainId,
  })

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const { validateRecipients, isLoading: isValidationLoading } =
    useMultiRecipientValidation({
      onError: useCallback(
        (error: Error) => {
          methods.setError('recipients', { message: error.message })
        },
        [methods],
      ),
    })

  const onSubmit = methods.handleSubmit(async (data) => {
    if (isOrgComplianceLoading) {
      toast.error('Failed to add recipients', {
        description: 'Still loading recipient information, please try again',
      })
      throw new Error('Tried to add recipients while loading information')
    }

    const resolvedRecipients = await validateRecipients(
      data.recipients,
      transferTokenMetadata.decimals,
    )

    if (resolvedRecipients) {
      const recipientsWithCompliantPaymentRelationIds = resolvedRecipients?.map(
        (recipient) => ({
          receiver: {
            address: recipient.address,
            compliantPaymentRelationId: payees.find(
              (payee) => payee.address === recipient.address,
            )?.id,
          },
          amount: recipient.amount,
        }),
      )
      onAdd(recipientsWithCompliantPaymentRelationIds)
      onClose()
    }
  })

  const recipients = useWatch({ control: methods.control, name: 'recipients' })
  const hasRecipientsChanged = useMemo(() => {
    return (
      recipients !== formRecipientsDefault ||
      formRecipientsDefault !== PLACEHOLDER_RECIPIENTS_TEXT
    )
  }, [recipients, formRecipientsDefault])

  const isDisabled =
    !recipients || recipients.trim().length === 0 || !hasRecipientsChanged

  const handleCSVParse = useCallback(
    (text: string) => {
      methods.clearErrors('recipients')
      methods.setValue('recipients', text, {
        shouldDirty: true,
        shouldTouch: true,
      })
    },
    [methods],
  )

  const handleCSVError = useCallback(
    (error: string) => {
      methods.setError('recipients', { message: error })
    },
    [methods],
  )

  return (
    <FormProvider {...methods}>
      <SplitsDialogContent
        title="Add multiple recipients"
        id="add-multi-recipient"
        variant="cta"
        cancelButton={{ text: 'Cancel', onClick: onClose }}
        actionButton={{
          text: 'Add',
          onClick: onSubmit,
          isDisabled,
        }}
        isLoading={isValidationLoading}
        maxWidth="xl"
      >
        <form onSubmit={onSubmit} className="flex flex-col space-y-4">
          <div className="flex flex-col space-y-2">
            <div>
              Enter one recipient per line using the format:{' '}
              <strong>address or ENS domain, amount</strong>.
            </div>
            <div>
              Amounts are denominated in {transferTokenMetadata?.symbol} with up
              to {transferTokenMetadata?.decimals} decimal places.
            </div>
          </div>
          <CSVUpload
            fileInputRef={fileInputRef}
            onParse={handleCSVParse}
            onError={handleCSVError}
          >
            <TextAreaInput<AddMultiRecipientFormValues>
              control={methods.control}
              inputName="recipients"
              placeholder={PLACEHOLDER_RECIPIENTS_TEXT}
              rows={6}
              autoFocus={shouldAutoFocus}
              resizable
            />
          </CSVUpload>
          <Message
            variant="info"
            action={{
              label: 'Upload',
              variant: 'link',
              onClick: (e) => {
                e.preventDefault()
                fileInputRef.current?.click()
              },
            }}
          >
            <>
              <div>
                {hasRecipientsChanged
                  ? 'Drag and drop or upload CSV to start over.'
                  : 'Drag and drop or click to upload a CSV.'}{' '}
                <SplitsLink
                  path="https://docs.google.com/spreadsheets/d/1ot0wyVp8ZeJFREgcKjHjR1KozBxtgVw8SBLQ7UVF-T4/edit?usp=sharing"
                  target="tab"
                  underline
                >
                  View template
                </SplitsLink>
              </div>
              {hasRecipientsChanged && (
                <div>CSV will replace existing recipients.</div>
              )}
            </>
          </Message>
        </form>
      </SplitsDialogContent>
    </FormProvider>
  )
}
